import {
  useUrlFilterState,
  useUrlRangeState,
  useUrlSortingState,
} from '../../../common/filtering/filters';
import { useState } from 'react';
import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { DripContact } from '../../../common/types';
import { DripContactTable } from '../../../components/dashboard/DripContactTable';
import { useMutation } from '@tanstack/react-query';
import { bulkUnsubscribeDripContacts } from '../../../common/api/dripContacts';
import { Box, Button, Modal, SpaceBetween } from '@cloudscape-design/components';
import { DripContactUploadModal } from '../../../components/DripContactUploadModal';
import { TaskMonitorModal } from '../../../components/TaskMonitorModal';
import {
  DripScopeFilter,
  useUrlDripScope,
} from '../../../components/dashboard/DripScopeFilter';

export default function View() {
  const { query, setQuery } = useUrlFilterState();
  const [textQuery, setTextQuery] = useState('');
  const past = true;
  const { sortingField, setSortingField, sortDirection, setSortDirection } = useUrlSortingState(
    'created_at',
    'desc',
  );
  const { range, setRange } = useUrlRangeState(past);
  const [now, setNow] = useState(new Date());
  const [selectedItems, setSelectedItems] = useState<DripContact[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskUuid, setTaskUuid] = useState<string | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const { scope, setCampaignUuid, setZipBlastUuid } = useUrlDripScope();

  const resourceName = 'Drip Contacts';

  const bulkUnsubscribe = useMutation({
    mutationFn: (uuids: string[]) => bulkUnsubscribeDripContacts(uuids),
    onSuccess: () => {
      setSelectedItems([]);
      setNow(new Date(new Date().getTime() + 1000));
      setBulkConfirmOpen(false);
    },
  });

  return (
    <DashboardViewLayout breadcrumbs={[{ text: resourceName, href: '' }]} contentType="table">
      <DripContactTable
        now={now}
        setNow={setNow}
        query={query}
        setQuery={setQuery}
        range={range}
        setRange={setRange}
        sortingField={sortingField}
        setSortingField={setSortingField}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        textQuery={textQuery}
        setTextQuery={setTextQuery}
        scope={scope}
        additionalActions={
          <SpaceBetween size="xs" direction="horizontal">
            <DripScopeFilter
              scope={scope}
              setCampaignUuid={setCampaignUuid}
              setZipBlastUuid={setZipBlastUuid}
            />
            <Button
              variant="normal"
              iconName="remove"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={selectedItems.length === 0 || bulkUnsubscribe.isPending}
              loading={bulkUnsubscribe.isPending}
            >
              Bulk unsubscribe
            </Button>
            <Button
              variant="primary"
              iconName="upload"
              onClick={() => {
                setUploadOpen(true);
              }}
              disabled={selectedItems.length !== 0 || bulkUnsubscribe.isPending}
            >
              Upload
            </Button>
          </SpaceBetween>
        }
      />
      <DripContactUploadModal
        open={uploadOpen}
        setOpen={setUploadOpen}
        onComplete={(data) => {
          setTaskUuid(data.uuid);
          setTaskOpen(true);
        }}
      />
      <TaskMonitorModal
        open={taskOpen}
        setOpen={setTaskOpen}
        task_uuid={taskUuid}
        onComplete={() => {
          setTaskUuid(null);
          setNow(new Date(new Date().getTime() + 1000));
        }}
      />
      <Modal
        onDismiss={() => {
          if (!bulkUnsubscribe.isPending) {
            setBulkConfirmOpen(false);
          }
        }}
        visible={bulkConfirmOpen}
        header="Confirm bulk unsubscribe"
        closeAriaLabel="Close modal"
        footer={
          <SpaceBetween size="xs" direction="horizontal">
            <Button
              variant="link"
              onClick={() => setBulkConfirmOpen(false)}
              disabled={bulkUnsubscribe.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={bulkUnsubscribe.isPending}
              disabled={selectedItems.length === 0 || bulkUnsubscribe.isPending}
              onClick={() =>
                bulkUnsubscribe.mutate(selectedItems.map((item) => item.uuid))
              }
            >
              Confirm unsubscribe
            </Button>
          </SpaceBetween>
        }
      >
        <Box variant="p">
          Are you sure you want to unsubscribe {selectedItems.length}{' '}
          {selectedItems.length === 1 ? 'contact' : 'contacts'} from drip campaigns?
        </Box>
      </Modal>
    </DashboardViewLayout>
  );
}
